import Fuse from 'fuse.js'

// A query that is only digits and phone punctuation, e.g. "(555) 987-6543".
const PHONE_QUERY = /^[\d\s().+-]+$/
const MIN_PHONE_QUERY_DIGITS = 7
const LAST4_QUERY = /^\d{4}$/
// Shorter words in a multi-word query are ignored; Fuse can't match them.
const MIN_WORD_LENGTH = 2

// True for a phone number query, which is searched as one number.
const isPhoneQuery = (text) =>
  PHONE_QUERY.test(text) &&
  text.replace(/\D/g, '').length >= MIN_PHONE_QUERY_DIGITS

// The words of a query that are searched; shorter ones are dropped.
const queryWords = (text) =>
  text.split(/\s+/).filter((word) => word.length >= MIN_WORD_LENGTH)

const formatPhoneNumber = (phoneNumber) => {
  const match = String(phoneNumber || '')
    .replace(/\D/g, '')
    .match(/^(\d{3})(\d{3})(\d{4})$/)
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : null
}

/**
 * Maps a v3 profile directory entry (GET v3/search/profiles) to a row of the
 * client search table, in the same shape as the server search rows.
 *
 * A joint account has one entry per holder sharing `user_profile_id`, so
 * `rowKey` (profile id + owner) is the unique row key, not `id`.
 */
export const toProfileDirectoryRow = (entry) => {
  // Digits only, no US country code; the first is the primary number.
  const phoneNumbers = entry.phone_numbers_national || []

  return {
    id: entry.user_profile_id,
    rowKey: `${entry.user_profile_id}-${entry.owner}`,
    // Some entries have no status; show and search them as UNKNOWN.
    accountStatus: entry.account_status || 'UNKNOWN',
    accountNumber: entry.account_number,
    // Brokers often know only the last 4, so it gets its own field.
    accountNumberLast4:
      entry.account_number_last4 ||
      (entry.account_number ? String(entry.account_number).slice(-4) : null),
    customerType: entry.customer_type,
    firstName: entry.given_name,
    lastName: entry.family_name,
    fullName: [entry.given_name, entry.family_name].filter(Boolean).join(' '),
    email: entry.email,
    phoneNumber:
      formatPhoneNumber(phoneNumbers[0]) ||
      formatPhoneNumber(entry.phone_number) ||
      entry.phone_number,
    // Space separated so a typed number can match any number on file.
    phoneDigits: phoneNumbers.join(' '),
    ssnLast4: entry.ssn_last4,
    dateOfBirth: entry.date_of_birth,
    owner: entry.owner,
  }
}

/**
 * Builds an in-memory search over profile directory rows (see
 * `toProfileDirectoryRow`). Results are ordered:
 *
 * 1. an exact account number,
 * 2. for a 4-digit query, the last 4 of an account number,
 * 3. for a 4-digit query, the last 4 of an SSN,
 * 4. for a phone number query (7+ digits), any number on file containing it,
 * 5. a fuzzy match (Fuse.js) on account number, name, email, phone,
 *    customer type and account status, where the account number's last 4
 *    carries the most weight.
 *
 * A multi-word query, e.g. "7954 wash", matches rows where every word matches
 * on its own, in any order and any field (a 4-digit word also matches the
 * last 4 of the account number or SSN). Account number last 4 matches still
 * come first, then SSN last 4, then the best combined fuzzy score.
 *
 * Each row appears once, and every match is returned. The `filter` search
 * option narrows the ranked rows, e.g. to approved accounts only.
 *
 * @param {Object[]} rows
 * @returns {{ search: (query: string, options?: { filter?: (row: Object)
 *   => boolean }) => Object[] }}
 */
export const createProfileDirectorySearch = (rows) => {
  const fuse = new Fuse(rows, {
    keys: [
      { name: 'accountNumberLast4', weight: 5 },
      { name: 'accountNumber', weight: 3 },
      { name: 'fullName', weight: 2 },
      { name: 'email', weight: 2 },
      { name: 'phoneDigits', weight: 1 },
      { name: 'customerType', weight: 1 },
      { name: 'accountStatus', weight: 1 },
    ],
    threshold: 0.3,
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 1,
  })

  // Rows matching one word, as rowKey -> Fuse score (0 is a perfect match).
  const wordMatches = (word) => {
    const scores = new Map(
      fuse.search(word).map(({ item, score }) => [item.rowKey, score])
    )
    if (LAST4_QUERY.test(word)) {
      for (const row of rows) {
        if (row.accountNumberLast4 === word || row.ssnLast4 === word) {
          scores.set(row.rowKey, 0)
        }
      }
    }
    return scores
  }

  // Every row matching all words, best first.
  const rankWords = (words) => {
    const matchesPerWord = words.map(wordMatches)
    const last4Words = words.filter((word) => LAST4_QUERY.test(word))
    const tier = (row) => {
      if (last4Words.includes(row.accountNumberLast4)) return 0
      if (last4Words.includes(row.ssnLast4)) return 1
      return 2
    }
    const score = (row) =>
      matchesPerWord.reduce((sum, matches) => sum + matches.get(row.rowKey), 0)

    return rows
      .filter((row) => matchesPerWord.every((m) => m.has(row.rowKey)))
      .map((row) => ({ row, tier: tier(row), score: score(row) }))
      .sort((a, b) => a.tier - b.tier || a.score - b.score)
      .map(({ row }) => row)
  }

  // Every row matching the query, best first.
  const rank = (text) => {
    const isPhone = isPhoneQuery(text)
    const words = queryWords(text)
    if (!isPhone && words.length > 1) return rankWords(words)

    // One usable word, e.g. "7954 w" searches "7954".
    const q = words.length === 1 ? words[0] : text
    const lower = q.toLowerCase()
    const digits = q.replace(/\D/g, '')

    const exact = rows.filter(
      (row) => String(row.accountNumber || '').toLowerCase() === lower
    )
    const isLast4 = LAST4_QUERY.test(q)
    const accountLast4 = isLast4
      ? rows.filter((row) => row.accountNumberLast4 === q)
      : []
    const ssnLast4 = isLast4 ? rows.filter((row) => row.ssnLast4 === q) : []
    const phone = isPhone
      ? rows.filter((row) => row.phoneDigits.includes(digits))
      : []
    const fuzzy = fuse.search(q).map((result) => result.item)

    const seen = new Set()
    return [...exact, ...accountLast4, ...ssnLast4, ...phone, ...fuzzy].filter(
      (row) => !seen.has(row.rowKey) && seen.add(row.rowKey)
    )
  }

  return {
    search(query, { filter } = {}) {
      const text = String(query || '').trim()
      if (!text) return []

      const ranked = rank(text)
      return filter ? ranked.filter(filter) : ranked
    },
  }
}

/**
 * Groups matched rows into one row per account. A joint account has a row per
 * holder; its grouped row keeps the first matched holder's fields, takes the
 * account number as `rowKey`, and lists every holder of the account (from
 * `allRows`, in their order) in `holders`, including holders the query didn't
 * match. Rows without an account number are never merged.
 *
 * @param {Object[]} matched - rows from a search, best first
 * @param {Object[]} [allRows] - every row, to find the other holders
 * @returns {Object[]} account rows, in the order each account first matched
 */
export const groupByAccount = (matched, allRows = matched) => {
  const holdersByAccount = new Map()
  for (const row of allRows) {
    if (!row.accountNumber) continue
    const holders = holdersByAccount.get(row.accountNumber) || []
    holders.push(row)
    holdersByAccount.set(row.accountNumber, holders)
  }

  const accounts = new Map()
  for (const row of matched) {
    const key = row.accountNumber || `row:${row.rowKey}`
    if (accounts.has(key)) continue
    const holders = (row.accountNumber &&
      holdersByAccount.get(row.accountNumber)) || [row]
    accounts.set(key, { ...row, rowKey: key, holders })
  }
  return [...accounts.values()]
}

/**
 * Sorts accounts by account number, for the list shown before a search.
 * Accounts without an account number come last.
 *
 * @param {Object[]} accounts - rows from `groupByAccount`
 * @returns {Object[]} a new array
 */
export const sortByAccountNumber = (accounts) =>
  [...accounts].sort((a, b) => {
    if (!a.accountNumber) return b.accountNumber ? 1 : 0
    if (!b.accountNumber) return -1
    return String(a.accountNumber).localeCompare(String(b.accountNumber))
  })

// Punctuation a digits-only term may span, e.g. "3210853" in "321-0853".
const PHONE_PUNCTUATION = /[\s().+-]/

// Marks every occurrence of a digits-only term in value, skipping phone
// punctuation between its digits.
const markDigits = (value, term, marks) => {
  const positions = []
  let chars = ''
  for (let i = 0; i < value.length; i++) {
    if (PHONE_PUNCTUATION.test(value[i])) continue
    positions.push(i)
    chars += value[i]
  }

  for (
    let at = chars.indexOf(term);
    at !== -1;
    at = chars.indexOf(term, at + 1)
  ) {
    const end = positions[at + term.length - 1]
    for (let i = positions[at]; i <= end; i++) marks[i] = true
  }
}

/**
 * Splits a displayed value into parts flagged by whether they match the
 * query, for highlighting search results. Uses the words the search uses,
 * matched case-insensitively wherever they appear; a digits-only word (and a
 * phone number query) also matches across phone punctuation, so "3210853"
 * marks "321-0853".
 *
 * Fuzzy matches on a typo aren't marked: the typed text isn't there.
 *
 * @param {*} value - the cell value; nullish renders as ''
 * @param {string} query
 * @returns {{ text: string, match: boolean }[]}
 */
export const highlightParts = (value, query) => {
  const text = value == null ? '' : String(value)
  const q = String(query || '').trim()
  if (!text || !q) return [{ text, match: false }]

  const terms = isPhoneQuery(q) ? [q.replace(/\D/g, '')] : queryWords(q)
  const lower = text.toLowerCase()
  const marks = new Array(text.length).fill(false)

  for (const term of terms.map((t) => t.toLowerCase())) {
    if (/^\d+$/.test(term)) {
      markDigits(lower, term, marks)
      continue
    }
    for (
      let at = lower.indexOf(term);
      at !== -1;
      at = lower.indexOf(term, at + 1)
    ) {
      marks.fill(true, at, at + term.length)
    }
  }

  const parts = []
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1]
    if (last && last.match === marks[i]) last.text += text[i]
    else parts.push({ text: text[i], match: marks[i] })
  }
  return parts
}
